import type { User, AvatarStatus, DepositMethodsConfig } from '~/stores/user'
import { getSessionId } from './useAuthApi'
import { writeTopbarCacheFromUser } from '~/utils/topbarCache'

// Response types from auth-session endpoint
interface AuthSessionResponse {
  authenticated: boolean
  session: {
    userId: string
    walletAddress: string | null
    depositWalletAddress: string | null
    depositWalletSolanaAddress: string | null
    depositWalletTronAddress: string | null
    depositWalletTonAddress: string | null
    tonMemoCode: string | null
    connectedAt: string
    lastActivity: string
    email?: string | null
    username?: string | null
    displayName?: string | null
    avatarUrl?: string | null
    avatarStatus?: AvatarStatus | null
    avatarUpdatedAt?: string | null
    fiatDepositsEnabled?: boolean
    bitoloDepositsEnabled?: boolean
    speedDepositsEnabled?: boolean
    depositMethods?: DepositMethodsConfig
  }
}

// Normalized response for internal use
interface MeResponse {
  user: User
}

interface SessionResponse {
  user: User
}

/**
 * User profile response from /user-profile endpoint
 */
export interface UserProfileResponse {
  id: string
  username: string
  /**
   * Equipped character nickname (player_equipped.display_name), null when unset.
   * Primary topbar label — the optimistic cache is refreshed from it on every
   * /user-profile fetch (see fetchProfile).
   */
  displayName: string | null
  shortBio: string | null
  avatarGender: 'male' | 'female'
  /** Server-gated avatar URL (null unless avatar_status === 'active'). */
  avatarUrl: string | null
  avatarStatus: AvatarStatus | null
  avatarUpdatedAt: string | null
  walletAddress: string
  email: string | null
  authMethod: string | null
  depositWalletAddress: string | null
  depositWalletSolanaAddress: string | null
  depositWalletTronAddress: string | null
  depositWalletTonAddress: string | null
  tonMemoCode: string | null
  verifications: {
    email: {
      verified: boolean
      method?: string
      verifiedAt?: string
      email?: string
    }
    social: {
      verified: boolean
      method?: string
      verifiedAt?: string
      handle?: string
    }
  }
  freeBets: {
    available: number
    total: number
    /**
     * True when the user has a prior FREE_BET_BLOCKED anti-abuse event.
     * Surfaces that offer earning a free bet (header pill, Earn-Free-Bets
     * popover) hide the affordance entirely when this is set — the backend
     * will reject any grant attempt anyway.
     */
    blocked: boolean
    bets: Array<{
      betNumber: number
      status: string
      unlockRequirement: string
      amountUsd?: number
      expiresAt?: string
    }>
  }
  kycLevel: number
  /**
   * The user's stored UI language (`users.language`), e.g. 'en' | 'uk'.
   * NOT NULL DEFAULT 'en' server-side, so never empty; the language-sync flow
   * pushes the active UI locale whenever it differs from this stored value.
   */
  language: string
}

/**
 * Response from /user-link-method endpoint
 */
export interface LinkMethodResponse {
  success: boolean
  method: string
  verificationMethod?: string
  freeBetGranted: boolean
  freeBetError?: string
}

/**
 * Fetch current user profile.
 * GET /auth-session
 *
 * Maps auth-session response to User format.
 */
export async function fetchMe(accessToken: string): Promise<MeResponse> {
  const config = useRuntimeConfig()
  const sessionId = getSessionId()

  const result = await $fetch<AuthSessionResponse>(`${config.public.apiBase}/auth-session`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId && { 'X-Session-Id': sessionId }),
    },
  })

  // Every field /auth-session sends is mapped: the result goes straight through
  // setUser(), so anything dropped here is actively erased from the store.
  return {
    user: {
      id: result.session.userId,
      walletAddress: result.session.walletAddress,
      depositWalletAddress: result.session.depositWalletAddress,
      depositWalletSolanaAddress: result.session.depositWalletSolanaAddress,
      depositWalletTronAddress: result.session.depositWalletTronAddress,
      depositWalletTonAddress: result.session.depositWalletTonAddress,
      tonMemoCode: result.session.tonMemoCode,
      email: result.session.email ?? null,
      username: result.session.username ?? null,
      displayName: result.session.displayName ?? null,
      avatarUrl: result.session.avatarUrl ?? null,
      avatarStatus: result.session.avatarStatus ?? null,
      avatarUpdatedAt: result.session.avatarUpdatedAt ?? null,
      fiatDepositsEnabled: result.session.fiatDepositsEnabled ?? false,
      bitoloDepositsEnabled: result.session.bitoloDepositsEnabled ?? false,
      speedDepositsEnabled: result.session.speedDepositsEnabled ?? false,
      depositMethods: result.session.depositMethods,
    },
  }
}

/**
 * Refresh/validate session.
 * GET /auth-session
 */
export async function fetchSession(accessToken: string): Promise<SessionResponse> {
  const config = useRuntimeConfig()
  const sessionId = getSessionId()

  const result = await $fetch<AuthSessionResponse>(`${config.public.apiBase}/auth-session`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId && { 'X-Session-Id': sessionId }),
    },
  })

  // Map session format to user format
  return {
    user: {
      id: result.session.userId,
      walletAddress: result.session.walletAddress,
      depositWalletAddress: result.session.depositWalletAddress,
      depositWalletSolanaAddress: result.session.depositWalletSolanaAddress,
      depositWalletTronAddress: result.session.depositWalletTronAddress,
      depositWalletTonAddress: result.session.depositWalletTonAddress,
      tonMemoCode: result.session.tonMemoCode,
      email: null,
    },
  }
}

/**
 * Fetch full user profile.
 * GET /user-profile
 */
export async function fetchProfile(accessToken: string): Promise<UserProfileResponse> {
  const config = useRuntimeConfig()

  const profile = await $fetch<UserProfileResponse>(`${config.public.apiBase}/user-profile`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  // Refresh the optimistic topbar cache from the fresh profile. This is the
  // canonical name-edit path: profile.vue re-fetches /user-profile right after
  // saveNickname, so the next reload's optimistic paint shows the new nickname
  // instead of the stale account handle. Identity-only fields; avatarUrl is
  // already server-gated by the edge function (spec C1). Render-only.
  writeTopbarCacheFromUser({
    id: profile.id,
    username: profile.username,
    displayName: profile.displayName,
    avatarUrl: profile.avatarUrl,
    avatarStatus: profile.avatarStatus,
  })

  return profile
}

/**
 * Persist the authenticated user's chosen UI language.
 * POST /user-language  body: { language }
 *
 * The backend reads `users.language` to localize off-band messages (emails,
 * Telegram/WhatsApp verification). Called by useUserLanguageSync on authed
 * load (when the stored value differs from the active locale) and on every
 * locale switch. Returns the raw `{ language }` echo from the endpoint.
 */
export async function setLanguage(
  accessToken: string,
  language: string,
): Promise<{ language: string }> {
  const config = useRuntimeConfig()

  return await $fetch<{ language: string }>(`${config.public.apiBase}/user-language`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: { language },
  })
}

/**
 * The 5 notification opt-in flags shown in Account → Communication.
 * All default TRUE for new accounts (resolved server-side).
 */
export interface NotificationPreferences {
  account_emails: boolean
  email_reminders: boolean
  telegram_reminders: boolean
  news_email: boolean
  news_telegram: boolean
}

/**
 * Read the user's notification preferences.
 * GET /notification-preferences
 */
export async function fetchNotificationPrefs(
  accessToken: string,
): Promise<NotificationPreferences> {
  const config = useRuntimeConfig()

  const res = await $fetch<{ preferences: NotificationPreferences }>(
    `${config.public.apiBase}/notification-preferences`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  return res.preferences
}

/**
 * Update a subset of the user's notification preferences.
 * POST /notification-preferences
 */
export async function updateNotificationPrefs(
  accessToken: string,
  partial: Partial<NotificationPreferences>,
): Promise<NotificationPreferences> {
  const config = useRuntimeConfig()

  const res = await $fetch<{ preferences: NotificationPreferences }>(
    `${config.public.apiBase}/notification-preferences`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: partial,
    },
  )
  return res.preferences
}

/**
 * Link a new auth method to the account.
 * POST /user-link-method
 *
 * `visitorId` is forwarded to the backend so the verification free-bet
 * grant (bet #1 for email, bet #2 for phone) inherits the device
 * fingerprint. Without it the shared-fingerprint anti-abuse gate in
 * `grantFreeBet` falls back to the per-IP rate limit only.
 */
export async function linkMethod(
  accessToken: string,
  params: {
    method: 'email' | 'phone'
    email?: string
    phone?: string
    verificationMethod?: 'telegram' | 'whatsapp'
    visitorId?: string | null
  }
): Promise<LinkMethodResponse> {
  const config = useRuntimeConfig()

  return await $fetch<LinkMethodResponse>(`${config.public.apiBase}/user-link-method`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: params,
  })
}

/**
 * Update display name.
 * POST /cosmetics-display-name
 */
export async function updateDisplayName(
  accessToken: string,
  displayName: string,
): Promise<{ success: boolean; displayName: string; wasFree: boolean }> {
  const config = useRuntimeConfig()
  const sessionId = getSessionId()

  return await $fetch(`${config.public.apiBase}/cosmetics-display-name`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId && { 'X-Session-Id': sessionId }),
    },
    body: { displayName },
  })
}

/**
 * Update short bio.
 * POST /cosmetics-short-bio
 *
 * Empty string clears the bio on the server.
 */
export async function updateShortBio(
  accessToken: string,
  shortBio: string,
): Promise<{ success: boolean; shortBio: string | null }> {
  const config = useRuntimeConfig()
  const sessionId = getSessionId()

  return await $fetch(`${config.public.apiBase}/cosmetics-short-bio`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId && { 'X-Session-Id': sessionId }),
    },
    body: { shortBio },
  })
}

/**
 * Public profile of another player (for the profile-view modal).
 *
 * Each slot mirrors the EquippedCosmetic shape used by the character store,
 * so CharacterViewer can render an arbitrary player via the `previewEquipped`
 * prop without an extra catalog lookup.
 */
export interface PublicProfileSlot {
  id: string
  slug: string
  name: string
  rarity: string
  assetBundle: string
  thumbnailUrl: string | null
}

export interface PublicProfileEquipped {
  head: PublicProfileSlot | null
  torso: PublicProfileSlot | null
  legs: PublicProfileSlot | null
  shoes: PublicProfileSlot | null
  pet: PublicProfileSlot | null
}

export interface PublicProfileResponse {
  id: string
  /**
   * Resolved on the backend as `player_equipped.display_name → users.username
   * → 'Player'`. The public surface deliberately skips the wallet-prefix
   * fallback that lives in the shared `resolveDisplayName` helper.
   * No separate `username` field is exposed — every surface should use this.
   */
  displayName: string
  shortBio: string | null
  avatarUrl: string | null
  characterId: string
  equipped: PublicProfileEquipped
  stats: {
    bets: number
    wageredXaut: string
    topMultiplier: string | null
    topWinXaut: string | null
  }
}

/**
 * Fetch public profile by user id.
 * GET /user-profile-public/:userId
 */
export async function fetchPublicProfile(userId: string): Promise<PublicProfileResponse> {
  const config = useRuntimeConfig()
  return await $fetch<PublicProfileResponse>(`${config.public.apiBase}/user-profile-public/${userId}`)
}

/**
 * Player rounds history (for the per-player rounds tab).
 *
 * The cursor (`before`) is an ISO-8601 `placedAt` string — the backend keys
 * both its sort and its cursor on `bets.placed_at` so pagination stays
 * deterministic. Callers grab the cursor from the last item's `placedAt`.
 */
export interface PlayerRoundItem {
  roundNumber: number
  crashPoint: string
  betAmount: string
  cashoutMultiplier: string | null
  payout: string
  status: 'cashed_out' | 'lost'
  isFreeBet: boolean
  placedAt: string
}

export async function fetchPlayerRoundsHistory(params: {
  userId: string
  before?: string
  limit?: number
}): Promise<PlayerRoundItem[]> {
  const config = useRuntimeConfig()
  const search = new URLSearchParams({ userId: params.userId })
  if (params.before !== undefined) search.set('before', params.before)
  if (params.limit !== undefined) search.set('limit', String(params.limit))

  const result = await $fetch<{ history: PlayerRoundItem[] }>(
    `${config.public.apiBase}/player-rounds-history?${search.toString()}`,
  )
  return result.history
}

// Re-export types
export type { MeResponse, SessionResponse }
